import {
    ChangeDetectionStrategy,
    Component, DestroyRef,
    EventEmitter, inject,
    Input,
    OnInit,
    Output, SimpleChanges, ViewChild,
    ViewEncapsulation, OnChanges
} from '@angular/core';
import {Consumer} from '@emm/core/models/consumer.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {Venue} from '@emm/core/models/venue.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {BehaviorSubject, Observable} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatMenuTrigger} from '@angular/material/menu';
import {take} from 'rxjs/operators';
import {transformDateService} from '@emm/shared/pipes/formatTimezone.pipe';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-menu-banner',
    styleUrls: ['./menu-banner.component.scss'],
    templateUrl: './menu-banner.component.html',
    standalone: false
})
export class MenuBannerComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    @Input() effectiveDate: string;
    @Input() isFutureDate: boolean;
    @Input() selectedConsumer: Consumer;
    @Input() selectedSubmenu: string;
    @Input() selectedFeatureScope: string;
    @Input() isSubmenuCacheEnabled: boolean;
    @Input() isEmpty: boolean;
    @Input() readonly: boolean;
    @Input() selectedLocation: Observable<LocationSet>;
    @Input() selectedVenue: Venue;
    @Input() selectedLocationType: LocationSetType;
    @Input() selectedEffectiveDate: Observable<string>;
    @Input() enablePublishButton = false;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    @Input() downloadMenuTooltip: string;

    @Output() addOrEditChangeSet = new EventEmitter();
    @Output() viewChangeSets = new EventEmitter();
    @Output() clearSubmenuCache = new EventEmitter();
    today = new Date();
    isDateToday = false;
    @Output() publishButtonClicked = new EventEmitter<void>();
    @Output() downloadMenuButtonClicked = new EventEmitter<void | string>();
    @Output() downloadItemsReportButtonClicked = new EventEmitter<void | string>();

    @ViewChild('downloadMenuTrigger') downloadMenuTrigger: MatMenuTrigger;
    @ViewChild('downloadItemsReportTrigger') downloadItemsReportTrigger: MatMenuTrigger;

    locationTag$ = new BehaviorSubject<string>(null);

    menuTooltips = [];
    consumersAvailableForMenuDownload = [
        'MOBILE',
        'MEMBERSHIP_REWARDS',
        'ONLINE_ORDERING_DELIVERY',
        'ONLINE_ORDERING_PICKUP',
        'SAUCES_DRESSINGS',
        'SPOTLIGHT',
        'FOOD_TRUCK',
        'MASTER_REWARDS_MENU',
        'COMMERCEPOINT',
        'COMMERCEPOINT_CATERING',
        'DCB',
    ];
    consumersUnavailableForItemsReport = [
        'EARLY_ACCESS',
        'MASTER_REWARDS_MENU',
        'MMS_LICENSEE',
        'MENU_ALIGNMENT'
    ];
    menuOptions = [
        'Mobile App & PWO',
        '.com',
        '.com Delivery',
        'Order API',
        'UberEats',
        'DoorDash',
        'GrubHub',
    ];
    reportOptions = [
        'Mobile',
        'UberEats',
        'GrubHub',
        'DoorDash',
    ];

    chainReportOptions = ['Mobile'];

    constructor(
        private notificationService: NotificationService,
        private api: MenuApiService,
    ) {}

    ngOnInit(): void {
        this.selectedEffectiveDate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((date) => {
            this.isDateToday = new Date(date).getDate() === this.today.getDate();
        });
        this.menuTooltips = new Array(this.menuOptions.length).fill('');
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes) {
            this.enablePublishButton = this.isSuperAdmin || this.isAdmin;
            this.selectedLocation?.pipe(take(1)).subscribe((location) => {
                if (this.selectedConsumer?.tag === 'MOBILE' && location) {
                    this.loadMenuTooltips(location.tag);
                }
                this.locationTag$.next(location?.tag);
            });
        }
    }

    openMenuDownloadOptions(event: Event) {
        event.preventDefault();
        this.downloadMenuTrigger?.openMenu();
    }

    openItemsReportDownloadOptions(event: Event) {
        event.preventDefault();
        this.downloadItemsReportTrigger?.openMenu();
    }

    handlePublishButtonClicked() {
        this.publishButtonClicked.next();
    }

    handleDownloadMenuButtonClicked(index?: number, location?: string) {
        if (this.selectedConsumer?.tag === 'MOBILE' && location) {
            const endpoint = this.getMobileMenuEndpoint(index, location);
            this.downloadMenuButtonClicked.next(endpoint);
        } else {
            this.downloadMenuButtonClicked.next();
        }
    }

    handleDownloadItemsReportButtonClicked(index?: number, location?: string) {
        if (this.selectedConsumer?.tag === 'MOBILE') {
            const endpoint = this.getMobileReportEndpoint(index, location);
            this.downloadItemsReportButtonClicked.next(endpoint);
        } else {
            this.downloadItemsReportButtonClicked.next();
        }
    }

    getMobileMenuEndpoint(index: number, location: string) {
        let endpoint;
        if (this.selectedLocationType.tag === 'CHAIN') {
            location = 'CHAIN';
        }
        switch (this.menuOptions[index]) {
            case 'Mobile App & PWO':
                endpoint = `menu/v0.1/individual/${location}/3.2?format=client`;
                return endpoint;
            case '.com':
                endpoint = `menu/v0.1/individual/${location}/3.1?format=client`;
                return endpoint;
            case '.com Delivery':
                endpoint = `menu/v0.1/individual/delivery/${location}/3.1?format=client`;
                return endpoint;
            case 'Order API':
                endpoint = `menu/v0.1/individual/${location}/4.0?format=client`;
                return endpoint;
            case 'UberEats':
                if (location !== 'CHAIN') {
                    endpoint = `menu/v0.1/individual/ubereats/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            case 'GrubHub':
                if (location !== 'CHAIN') {
                    endpoint = `menu/v0.1/individual/grubhub/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            case 'DoorDash':
                if (location !== 'CHAIN') {
                    endpoint = `menu/v0.1/individual/doordash/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            default:
                break;
        }
        return endpoint;
    }

    getMobileReportEndpoint(index: number, location: string) {
        let endpoint;
        if (this.selectedLocationType.tag === 'CHAIN') {
            location = 'CHAIN';
        }
        switch (this.reportOptions[index]) {
            case 'Mobile':
                endpoint = `reports/items/MOBILE/${location}`;
                return endpoint;
            case 'Postmates':
                endpoint = `reports/items/${location}/4.0/client/individual/postmates`;
                return endpoint;
            case 'UberEats':
                endpoint = `reports/items/${location}/4.0/client/individual/ubereats`;
                return endpoint;
            case 'GrubHub':
                endpoint = `reports/items/${location}/4.0/client/individual/grubhub`;
                return endpoint;
            case 'DoorDash':
                endpoint = `reports/items/${location}/4.0/client/individual/doordash`;
                return endpoint;
            default:
                break;
        }
        return endpoint;
    }

    getLastPublishedEndpoint(index: number, location: string) {
        let endpoint;
        if (this.selectedLocationType.tag === 'CHAIN') {
            return null;
        }
        switch (this.menuOptions[index]) {
            case 'Mobile App & PWO':
                endpoint = `menu/metadata/individual/${location}/3.2?format=client`;
                return endpoint;
            case '.com':
                endpoint = `menu/metadata/individual/${location}/3.1?format=client`;
                return endpoint;
            case '.com Delivery':
                endpoint = `menu/metadata/individual/delivery/${location}/3.1?format=client`;
                return endpoint;
            case 'Order API':
                endpoint = `menu/metadata/individual/${location}/4.0?format=client`;
                return endpoint;
            case 'UberEats':
                if (location !== 'CHAIN') {
                    endpoint = `menu/metadata/individual/ubereats/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            case 'GrubHub':
                if (location !== 'CHAIN') {
                    endpoint = `menu/metadata/individual/grubhub/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            case 'DoorDash':
                if (location !== 'CHAIN') {
                    endpoint = `menu/metadata/individual/doordash/${location}/3.1?format=client`;
                    return endpoint;
                } else {
                    return null;
                }
            default:
                return null;
        }
    }

    getLastPublishedDateForMenu(index: number, location: string) {
        if (this.selectedLocationType.tag === 'CHAIN') {
            location = 'CHAIN';
        }
        const endpoint = this.getLastPublishedEndpoint(index, location);
        if (endpoint) {
            return this.api.getLastPublishedDateForMenu(endpoint)?.pipe(take(1)).subscribe({
                next: (response) => {
                    this.menuTooltips[index] = `Last Published ${transformDateService(response?.lastPublishDate)}`;
                },
                error: (error) => {
                    this.menuTooltips[index] = `We couldn't get the last published date.`;
                    if (error.status !== 404) {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            });
        };
    }

    loadMenuTooltips(location: string) {
        if (this.selectedFeatureScope === 'Viewer' && this.isDateToday) {
            for (let i = 0; i < this.menuOptions.length; i++) {
                this.getLastPublishedDateForMenu(i, location);
            }
        }
    }
}
